import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Mail, CheckCircle, ExternalLink } from "lucide-react";

interface EmailVerificationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  email: string;
}

export function EmailVerificationDialog({ 
  open, 
  onOpenChange, 
  email 
}: EmailVerificationDialogProps) {
  const [isEmailOpened, setIsEmailOpened] = useState(false);
  
  const handleOpenEmail = () => {
    setIsEmailOpened(true);
    
    // Try to open the user's default email client
    const gmailUrl = `https://mail.google.com/mail/u/0/#search/from:support@saasgama.com`;
    const outlookUrl = `https://outlook.live.com/mail/0/inbox`;
    
    // Detect email domain to suggest the right provider
    const emailDomain = email.split('@')[1]?.toLowerCase();
    
    if (emailDomain?.includes('gmail')) {
      window.open(gmailUrl, '_blank');
    } else if (emailDomain?.includes('outlook') || emailDomain?.includes('hotmail') || emailDomain?.includes('live')) {
      window.open(outlookUrl, '_blank');
    } else {
      // For other providers, just try the generic mailto
      window.open(`mailto:`, '_blank');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md mx-auto">
        <DialogHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <Mail className="w-8 h-8 text-green-600" />
          </div>
          <DialogTitle className="text-2xl font-bold text-gray-900">
            Check your email
          </DialogTitle>
          <DialogDescription className="text-base text-gray-600 mt-2">
            We've sent a verification link to
          </DialogDescription>
        </DialogHeader>
        
        <div className="text-center py-4">
          <div className="bg-blue-50 rounded-lg p-4 mb-6">
            <p className="font-semibold text-blue-900 text-lg">{email}</p>
          </div>
          
          <div className="space-y-4 text-sm text-gray-600 mb-6">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
              <p className="text-left">Click the verification link in the email to continue setting up your account</p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
              <p className="text-left">The link will expire in 24 hours for security</p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
              <p className="text-left">Check your spam folder if you don't see the email</p>
            </div>
          </div>
          
          <div className="space-y-3">
            <Button 
              onClick={handleOpenEmail}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium h-12"
            >
              <Mail className="w-4 h-4 mr-2" />
              Open Email App
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
            
            <Button 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              className="w-full h-12"
            >
              I'll check later
            </Button>
          </div>
          
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500">
              Didn't receive the email? Check your spam folder or{" "}
              <button 
                onClick={() => onOpenChange(false)}
                className="text-blue-600 hover:text-blue-700 underline"
              >
                try again with a different email
              </button>
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}